import { createSlice } from "@reduxjs/toolkit";
import { InitialIntegrationReducer } from "../../../../_services/store/InitialIntegrationReducer";
import { integrationReducerActions } from "../../../../_commons/integration.reducer";

const initialNewConfiguration = {
    clientUUID: "",
    amazonSellerId: "",
};

export const initialAmazonOrderReducer = {
    ...new InitialIntegrationReducer(),
    isLoading: true,
    newConfiguration: { ...initialNewConfiguration },
    importData: null,
    dataFileToImport: null,

    isGetReportsLoading: false,
    getReportsErrorMessage: null,
    reports: null,
    deleteReportLoading: false,
    deleteReportErrorMessage: null,
    reprocessReportLoading: false,
    reprocessReportErrorMessage: null,
    resetReportLoading: false,
    resetReportErrorMessage: null,
};

const amazonOrderSlice = createSlice({
    name: "AmazonOrder",
    initialState: initialAmazonOrderReducer,
    reducers: {
        ...integrationReducerActions,
        toggleNewIntegration(state) {
            state.createConfigurationModal = !state.createConfigurationModal;
            state.amazonSellerId = null;
        },
        toggleDeleteIntegration(state, action) {
            state.configurationToBeDeleted = action.payload;
            state.deleteConfigurationModal = !state.deleteConfigurationModal;
        },
        toggleAdvancedSettingsModal(state) {
            state.advancedSettingsModal = !state.advancedSettingsModal;
        },

        setEditConfiguration(state, action) {
            state.editConfiguration = action.payload;
            
        },
        setUnsavedChanges(state, action) {
            state.unsavedChanges = action.payload;
        },

        setUploadedDataFile(state, action) {
            state.dataFileToImport = action.payload;
        },

        addNewConfigurationRequest(state) {
            state.submitted = true;
            state.isLoading = true;
            state.createErrorMessage = null;
        },
        addNewConfigurationSuccess(state, action) {
            let config = action.payload;
            let configs = state.configurations;
            if (configs.length > 0) {
                configs = configs.concat(config);
                configs.sort((a, b) =>
                    a.clientName.toLowerCase() > b.clientName.toLowerCase()
                        ? 1
                        : a.clientName.toLowerCase() < b.clientName.toLowerCase()
                          ? -1
                          : 0
                );
            } else {
                configs = [config];
            }
            state.submitted = false;
            state.isLoading = false;
            state.createConfigurationModal = false;
            state.configurations = configs;
            state.newConfiguration = initialNewConfiguration;
        },
        addNewConfigurationFailure(state, action) {
            state.submitted = false;
            state.isLoading = false;
            state.createErrorMessage = action.payload;
        },

        getConfigurationsForSelectedOrganizationRequest(state) {
            state.isLoading = true;
            state.configurations = [];
            state.successMessage = null;
        },
        getConfigurationsForSelectedOrganizationSuccess(state, action) {
            state.isLoading = false;
            state.configurations = action.payload;
            state.errorMessage = null;
        },
        getConfigurationsForSelectedOrganizationFailure(state, action) {
            state.isLoading = false;
            state.configurations = [];
            state.errorMessage = action.payload;
        },

        determineImportScreenStateRequest(state) {
            state.isLoading = true;
            state.commons = null;
            state.configuration = null;
            state.editConfiguration = null;
            state.errorMessage = null;
            state.importData = null;
            state.unsavedChanges = false;
            state.successMessage = null;
            state.importSuccessMessage = null;
            state.importErrorMessage = null;
            state.advancedSettingsModal = false;
            state.updateErrorMessage = undefined;
        },
        determineImportScreenStateSuccess(state, action) {
            state.isLoading = false;
            state.commons = action.payload.commons;
            state.configuration = action.payload.configuration;
            state.editConfiguration = action.payload.configuration;
            state.errorMessage = null;
        },
        determineImportScreenStateFailure(state, action) {
            state.isLoading = false;
            state.configuration = null;
            state.errorMessage = action.payload;
        },
        /**
         * Writes the re-detected VCS status into an import screen that is already mounted.
         *
         * determineImportScreenStateRequest/Success are wrong for a refresh triggered by a finished import: the
         * request case wipes the screen and its import messages, and the success case replaces editConfiguration,
         * which is the unsaved draft the screen renders from. This case therefore writes only the flag the header
         * badge reads and leaves the draft, the commons and the import result untouched.
         */
        refreshVCSStatusSuccess(state, action) {
            // Fails closed on purpose. A refresh can resolve after the user has moved to another Amazon
            // configuration, and a response that cannot be attributed to the configuration on screen - no requested
            // UUID, no configuration in the payload, or a different one loaded meanwhile - is discarded.
            const requested = action.payload.configurationUUID;
            const refreshed = action.payload.configuration;
            if (!requested || !refreshed || state.editConfiguration?.externalUUID !== requested) {
                return;
            }
            // The screen renders from editConfiguration, so the badge only moves if the draft is updated. vcsActive
            // is detected by the backend and never edited here, so writing it loses no user edit, and the update
            // mapper ignores the field, so saving the draft afterwards cannot write it back either.
            state.editConfiguration.vcsActive = refreshed.vcsActive;
            if (state.configuration?.externalUUID === requested) {
                state.configuration.vcsActive = refreshed.vcsActive;
            }
        },
        getConfigurationRequest(state) {
            state.isLoading = true;
            state.configuration = null;
            state.errorMessage = null;
            state.importData = null;
            state.unsavedChanges = false;
            state.successMessage = null;
        },
        getConfigurationSuccess(state, action) {
            state.isLoading = false;
            state.configuration = action.payload;
            state.errorMessage = null;
        },
        getConfigurationFailure(state, action) {
            state.isLoading = false;
            state.configuration = null;
            state.errorMessage = action.payload;
        },
        // Keeps the saved configuration through the save: cancel and the consolidation notice compare against it.
        updateConfigurationRequest(state) {
            state.isUpdateLoading = true;
            state.updateErrorMessage = null;
            state.successMessage = null;
        },
        updateConfigurationSuccess(state, action) {
            state.isUpdateLoading = false;
            state.configuration = action.payload;
            state.successMessage = "Speichern erfolgreich.";
            state.advancedSettingsModal = false;
        },
        updateConfigurationFailure(state, action) {
            state.isUpdateLoading = false;
            state.updateErrorMessage = action.payload;
        },

        deleteConfigurationRequest(state) {
            state.isDeleteLoading = true;
            state.deleteErrorMessage = null;
        },
        deleteConfigurationSuccess(state, action) {
            if (state.configurations.length === 1) {
                state.configurations = [];
            } else if (state.configurations.length > 1) {
                const configIndex = state.configurations.findIndex(
                    (configuration) => configuration.externalUUID === action.payload
                );
                state.configurations = state.configurations.splice(configIndex, 1);
            }
            state.configurationToBeDeleted = null;
            state.deleteConfigurationModal = false;
            state.isDeleteLoading = false;
        },
        deleteConfigurationFailure(state, action) {
            state.isDeleteLoading = false;
            state.deleteErrorMessage = action.payload;
        },

        uploadDataFileAndStartImportRequest(state) {
            state.isImportLoading = true;
            state.importErrorMessage = null;
            state.importSuccessMessage = null;
        },
        uploadDataFileAndStartImportSuccess(state, action) {
            state.isImportLoading = false;
            state.importSuccessMessage = action.payload.notifications
                ? action.payload.notifications.map((notification) => notification.message)
                : undefined;
            state.dataFileToImport = null;
            state.importData = null;
            state.errorMessage = null;
        },
        uploadDataFileAndStartImportFailure(state, action) {
            state.isImportLoading = false;
            state.dataFileToImport = null;
            state.importErrorMessage = action.payload;
        },

        uploadDataFileAndValidateRequest(state) {
            state.isImportLoading = true;
            state.importData = null;
            state.importErrorMessage = null;
            state.importSuccessMessage = null;
        },
        uploadDataFileAndValidateSuccess(state, action) {
            state.isImportLoading = false;
            state.importData = action.payload;
            state.dataFileToImport = null;
            state.errorMessage = null;
        },
        uploadDataFileAndValidateFailure(state, action) {
            state.isImportLoading = false;
            state.dataFileToImport = null;
            state.importErrorMessage = action.payload;
            state.importData = null;
        },
        getReportsRequest(state) {
            state.isGetReportsLoading = true;
            state.getReportsErrorMessage = null;
        },
        getReportsSuccess(state, action) {
            state.isGetReportsLoading = false;
            state.reports = action.payload;
        },
        getReportsFailure(state, action) {
            state.isGetReportsLoading = false;
            state.getReportsErrorMessage = action.payload;
        },
        getReportItemsRequest(state) {
            state.isgetReportItemsLoading = true;
            state.getReportItemsErrorMessage = null;
        },
        getReportItemsSuccess(state, action) {
            state.isgetReportItemsLoading = false;
            // This data is not handled in the reducer but the component itself
        },
        getReportItemsFailure(state, action) {
            state.isgetReportItemsLoading = false;
            state.getReportItemsErrorMessage = action.payload;
        },
        deleteReportRequest(state) {
            state.deleteReportLoading = true;
            state.deleteReportErrorMessage = null;
        },
        deleteReportSuccess(state, action) {
            state.deleteReportLoading = false;
        },
        deleteReportFailure(state, action) {
            state.deleteReportLoading = false;
            state.deleteReportErrorMessage = action.payload;
        },
        reprocessReportRequest(state) {
            state.reprocessReportLoading = true;
            state.reprocessReportErrorMessage = null;
        },
        reprocessReportSuccess(state, action) {
            state.reprocessReportLoading = false;
        },
        reprocessReportFailure(state, action) {
            state.reprocessReportLoading = false;
            state.reprocessReportErrorMessage = action.payload;
        },
        resetReportRequest(state) {
            state.resetReportLoading = true;
            state.resetReportErrorMessage = null;
        },
        resetReportSuccess(state, action) {
            state.resetReportLoading = false;
        },
        resetReportFailure(state, action) {
            state.resetReportLoading = false;
            state.resetReportErrorMessage = action.payload;
        },
        resetImportDataErrors(state) {
            state.deleteReportErrorMessage = null;
            state.reprocessReportErrorMessage = null;
            state.resetReportErrorMessage = null;
        },

        setNewConfiguration(state, action) {
            state.newConfiguration = action.payload;
        },

        resetSuccessMessages(state) {
            state.successMessage = null;
        },
        resetUpdateErrorMessage(state) {
            state.updateErrorMessage = null;
        },
        triggerImportRequest(state) {
            state.isCatchupImportLoading = true;
            state.catchupImportErrorMessage = null;
        },
        triggerImportSuccess(state, action) {
            state.isCatchupImportLoading = false;
            state.catchupImportSuccessMessage = action.payload.successMessage;
        },
        triggerImportFailure(state, action) {
            state.isCatchupImportLoading = false;
            state.catchupImportErrorMessage = action.payload;
        },
        fetchSellerIdRequest(state) {
            state.isSellerIdLoading = true;
            state.getSellerIdErrorMessage = null;
        },
        fetchSellerIdSuccess(state, action) {
            state.isSellerIdLoading = false;
            state.newConfiguration.amazonSellerId = action.payload;
        },
        fetchSellerIdFailure(state, action) {
            state.isSellerIdLoading = false;
            state.getSellerIdErrorMessage = action.payload;
        },

    },
});

export default amazonOrderSlice;
